import React, { useEffect, useState, useMemo } from "react";
import DOMPurify from "dompurify";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  Avatar,
} from "@mui/material";
import {
  fetchUserCertifications,
  createCertification,
  updateCertification,
  deleteCertification,
  fetchAllowedCertifications,
  fetchUserMemberships,
  fetchNextClassesForAllowedCerts,
  downloadCertificationDocument,
  fetchTenantUsersByRole,
  fetchCertificationsByUserIds,
  fetchCompletedCourses,
  downloadCertificatePdf,
} from "../api/apiClient";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTenant } from "../contexts/TenantContext";
import AhaEcardsSection from "../components/aha/AhaEcardsSection";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import SchoolIcon from "@mui/icons-material/School";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VerifiedIcon from "@mui/icons-material/Verified";
import LinkIcon from "@mui/icons-material/Link";
import SearchIcon from "@mui/icons-material/Search";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PeopleIcon from "@mui/icons-material/People";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import {
  AdminCertificationTypesView,
} from "./CertificationTypeManagementView";
import { parseLocalDate } from "../utils/dateUtils";
import CertificationShell from "../components/certifications/CertificationShell";
import CertificationCard from "../components/certifications/CertificationCard";
import StudentCertificationsView from "../components/certifications/student/StudentCertificationsView";

// ─── Design System ───────────────────────────────────────────────────────────
const DS = {
  ink: "var(--color-ink)",
  ink2: "var(--color-ink-2)",
  ink3: "var(--color-ink-3)",
  ink4: "var(--color-ink-4)",
  line: "var(--color-hairline-cloud)",
  line2: "var(--color-surface-press-light)",
  line3: "var(--color-surface-2)",
  surface: "#FFFFFF",
  surface2: "var(--color-surface-2)",
  surface3: "var(--color-surface-press-light)",
  pageBg: "var(--color-surface-2)",

  primary50: "var(--color-primary-10)",
  live: "#C2410C",
  live50: "rgba(194,65,12,.10)",
  liveBg: "var(--color-warn-bg)",
  blue: "var(--color-primary)",
  blue50: "rgba(37,99,235,.10)",
  blue100: "rgba(37,99,235,.20)",
  purple: "var(--color-accent-violet)",
  purple50: "rgba(107,79,187,.10)",
  danger: "#B42318",
  danger50: "rgba(180,35,24,.08)",
  danger100: "rgba(180,35,24,.18)",
  grey: "var(--color-ink-3)",
  greyBg: "rgba(107,114,128,.10)",

  // Legacy aliases for dialog code
  surfaceContainerLowest: "#FFFFFF",
  surfaceContainerLow: "var(--color-surface-2)",
  surfaceContainerHigh: "var(--color-hairline-cloud)",
  surfaceContainerHighest: "var(--color-hairline-cloud)",
  onSurface: "var(--color-ink)",
  onSurfaceVariant: "var(--color-ink-2)",
  outline: "var(--color-ink-3)",
  outlineVariant: "var(--color-hairline-cloud)",
  secondary: "#15803D",
  secondaryContainer: "#85f6e5",
};

function useDS() {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary.main;
  const accent = theme.palette.warning.main;
  return {
    ...DS,
    primary,
    primary700: theme.palette.primary.dark,
    primaryContainer: theme.palette.primary.dark,
    primary10: alpha(primary, 0.06),
    green: secondary,
    green50: alpha(secondary, 0.10),
    green100: alpha(secondary, 0.20),
    amber: accent,
    amber50: alpha(accent, 0.10),
    amber100: alpha(accent, 0.22),
    secondary,
    headlineFont: theme.typography.h4.fontFamily,
  };
}

// ─── Helper Functions ───────────────────────────────────────────────────────
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = parseLocalDate(dateStr);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.ceil((d - now) / 86400000);
}

function certStatus(cert) {
  if (!cert) return "missing";
  const days = daysUntil(cert.expires);
  if (days === null) return "active"; // on file and never expires
  if (days < 0) return "expired";
  if (days <= 30) return "expiring";
  return "active";
}

const STATUS_CFG = {
  active: {
    color: "success",
    label: "Active",
    bgColor: "var(--color-success-bg)",
    textColor: "#166534",
    dsBg: "rgba(21, 128, 61, 0.12)",
    dsText: "#15803D",
    dsBorder: "rgba(21, 128, 61, 0.25)",
  },
  expiring: {
    color: "warning",
    label: "Expiring Soon",
    bgColor: "rgba(217, 119, 6, 0.08)",
    textColor: "var(--color-warn)",
    dsBg: "rgba(181, 101, 29, 0.10)",
    dsText: "#B5651D",
    dsBorder: "rgba(181, 101, 29, 0.40)",
  },
  expired: {
    color: "error",
    label: "Expired",
    bgColor: "var(--color-danger-bg)",
    textColor: "#991b1b",
    dsBg: "rgba(180, 35, 24, 0.12)",
    dsText: "#B42318",
    dsBorder: "rgba(180, 35, 24, 0.25)",
  },
  missing: {
    color: "default",
    label: "Not on File",
    bgColor: "#f3f4f6",
    textColor: "var(--color-ink-3)",
    dsBg: "rgba(107, 114, 128, 0.12)",
    dsText: "var(--color-ink-3)",
    dsBorder: "rgba(107, 114, 128, 0.25)",
  },
};

function fmtDate(d) {
  if (!d) return "\u2014";
  return parseLocalDate(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function resolveEvidenceUrl(url) {
  if (!url) return url;
  if (url.startsWith("/api/")) {
    const apiBase = process.env.REACT_APP_API_URL;
    return apiBase ? `${apiBase}${url}` : url;
  }
  return url;
}

const DOCUMENT_PATH = /^\/api\/v1\/users\/(\d+)\/certifications\/(\d+)\/document$/;

/** Opens a certification's evidence: uploaded documents are fetched with the user's auth, links open directly. */
async function openEvidenceDocument(url) {
  if (!url) return;
  const uploaded = url.match(DOCUMENT_PATH);
  if (!uploaded) {
    window.open(url, "_blank");
    return;
  }
  try {
    const blob = await downloadCertificationDocument(uploaded[1], uploaded[2]);
    const blobUrl = URL.createObjectURL(blob);
    const win = window.open(blobUrl, "_blank");
    if (win) {
      win.addEventListener("load", () => URL.revokeObjectURL(blobUrl), { once: true });
    }
  } catch {
    window.open(resolveEvidenceUrl(url), "_blank");
  }
}

function findCertForType(myCerts, certType) {
  return (
    myCerts.find((c) => c.certTypeId === certType.id) ||
    myCerts.find((c) => c.name === certType.name) ||
    null
  );
}

function initials(name) {
  const titles = new Set(["capt","capt.","captain","lt","lt.","lieutenant","chief","dr","dr.","mr","mr.","ms","ms.","mrs","mrs."]);
  const parts = (name || "").replace(/[^A-Za-z. ]/g, "").split(/[. ]+/).filter(Boolean)
    .filter((w) => !titles.has(w.toLowerCase()));
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0] || "X").slice(0, 2).toUpperCase();
}

// ─── Shared Components ───────────────────────────────────────────────────────

function ScopeBar({ scopes, activeScope, onChange }) {
  const DS = useDS();
  return (
    <Box sx={{ display: "inline-flex", gap: "4px", bgcolor: DS.surface3, p: "4px", borderRadius: "10px", border: `1px solid ${DS.line2}`, mb: 2.75 }}>
      {scopes.map((s) => (
        <Button
          key={s.id}
          onClick={() => onChange(s.id)}
          size="small"
          sx={{
            fontSize: "13px",
            fontWeight: 600,
            color: activeScope === s.id ? DS.primary : DS.ink3,
            bgcolor: activeScope === s.id ? "#fff" : "transparent",
            boxShadow: activeScope === s.id ? "0 1px 0 rgba(20,24,40,.04), 0 1px 3px rgba(20,24,40,.06)" : "none",
            borderRadius: "8px",
            textTransform: "none",
            px: 1.5,
            py: 0.5,
            minWidth: 0,
            gap: "8px",
            "&:hover": { color: DS.ink, bgcolor: activeScope === s.id ? "#fff" : "transparent" },
          }}
        >
          {s.icon}
          {s.label}
        </Button>
      ))}
    </Box>
  );
}

function Banner({ tone, icon, text, cta, onCta }) {
  const DS = useDS();
  if (!text) return null;
  const toneCfg = {
    green:  { bg: DS.green50, border: DS.green100, color: "#11632F", iconBg: alpha(DS.green, 0.16), iconColor: DS.green },
    amber:  { bg: DS.amber50, border: DS.amber100, color: "#8A4E16", iconBg: alpha(DS.amber, 0.16), iconColor: DS.amber },
    danger: { bg: DS.danger50, border: DS.danger100, color: "#8E1C13", iconBg: "rgba(180,35,24,.14)", iconColor: DS.danger },
    blue:   { bg: DS.blue50, border: DS.blue100, color: "var(--color-primary)", iconBg: "rgba(37,99,235,.16)", iconColor: DS.blue },
  };
  const cfg = toneCfg[tone] || toneCfg.amber;

  return (
    <Paper elevation={0} sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, gap: "14px", p: "14px 18px", borderRadius: "12px", mb: 2.75, border: `1px solid ${cfg.border}`, bgcolor: cfg.bg }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "14px" }}>
        <Box sx={{ width: 38, height: 38, borderRadius: "8px", display: "grid", placeItems: "center", flexShrink: 0, bgcolor: cfg.iconBg, color: cfg.iconColor }}>
          {icon === "check" ? <CheckCircleIcon sx={{ fontSize: 20 }} /> :
           icon === "clock" ? <AccessTimeIcon sx={{ fontSize: 20 }} /> :
           icon === "alert" ? <WarningAmberIcon sx={{ fontSize: 20 }} /> :
           icon === "users" ? <PeopleIcon sx={{ fontSize: 20 }} /> :
           <WarningAmberIcon sx={{ fontSize: 20 }} />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ color: cfg.color, fontWeight: 600, fontSize: "13.5px", lineHeight: 1.4 }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }} />
        </Box>
      </Box>
      {cta && onCta && (
        <Button
          size="small"
          variant={tone === "green" ? "outlined" : "contained"}
          onClick={onCta}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "12.5px",
            borderRadius: "10px",
            flexShrink: 0,
            width: { xs: "100%", sm: "auto" },
            bgcolor: tone === "green" ? "transparent" : DS.primary,
            backgroundImage: "none",
            color: tone === "green" ? cfg.color : "#fff",
            borderColor: tone === "green" ? cfg.border : DS.primary,
            "&:hover": { bgcolor: tone === "green" ? "rgba(0,0,0,0.02)" : DS.primary700 },
          }}
        >
          {cta}
        </Button>
      )}
    </Paper>
  );
}

function PersonRow({ person, kind, onClick }) {
  const DS = useDS();
  const gridCols = kind === "agency" ? "2.2fr 1.3fr 1.6fr 1fr 40px" : "2.2fr 1.8fr 1.4fr 1fr 40px";
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "grid", gridTemplateColumns: gridCols, alignItems: "center", gap: "16px",
        px: 2.5, py: 1.875, borderBottom: `1px solid ${DS.line2}`, cursor: "pointer",
        transition: "background .12s", "&:hover": { bgcolor: DS.surface2 }, "&:last-child": { borderBottom: 0 },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
        <Avatar sx={{ width: 38, height: 38, borderRadius: "11px", fontSize: "13px", fontWeight: 700, color: "#fff", background: "linear-gradient(135deg,#2A4A75,#0A4A8F)" }}>
          {initials(person.name)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: "13.5px", fontWeight: 700, color: DS.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{person.name}</Typography>
          <Typography sx={{ fontSize: "12px", color: DS.ink3, mt: "1px" }}>{person.meta}</Typography>
        </Box>
      </Box>

      <Box>
        <Typography sx={{ fontSize: "11px", color: DS.ink3, display: { xs: "block", md: "none" }, mb: 0.5 }}>
          {kind === "agency" ? "Station" : "Assigned class"}
        </Typography>
        <Typography sx={{ fontSize: "13px", color: DS.ink, fontWeight: 600 }}>{kind === "agency" ? person.station : person.cls}</Typography>
      </Box>

      <Box>
        <Typography sx={{ fontSize: "11px", color: DS.ink3, display: { xs: "block", md: "none" }, mb: 0.5 }}>Credentials</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Chip size="small" sx={{ fontSize: "11px", fontWeight: 700, px: "9px", py: "3px", borderRadius: "7px", fontVariantNumeric: "tabular-nums", bgcolor: DS.green50, color: DS.green, gap: "5px" }} label={<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "5px" }}><Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "currentColor" }} />{person.g}</Box>} />
          {person.a > 0 && <Chip size="small" sx={{ fontSize: "11px", fontWeight: 700, px: "9px", py: "3px", borderRadius: "7px", fontVariantNumeric: "tabular-nums", bgcolor: DS.amber50, color: DS.amber, gap: "5px" }} label={<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "5px" }}><Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "currentColor" }} />{person.a}</Box>} />}
          {person.d > 0 && <Chip size="small" sx={{ fontSize: "11px", fontWeight: 700, px: "9px", py: "3px", borderRadius: "7px", fontVariantNumeric: "tabular-nums", bgcolor: DS.danger50, color: DS.danger, gap: "5px" }} label={<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "5px" }}><Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "currentColor" }} />{person.d}</Box>} />}
        </Box>
      </Box>

      <Box>
        <Typography sx={{ fontSize: "11px", color: DS.ink3, display: { xs: "block", md: "none" }, mb: 0.5 }}>Status</Typography>
        <Chip
          size="small"
          label={person.statusLabel}
          sx={{
            fontSize: "10.5px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
            borderRadius: "999px", border: "1px solid transparent", whiteSpace: "nowrap",
            bgcolor: person.status === "green" ? DS.green50 : person.status === "amber" ? DS.amber50 : DS.danger50,
            color: person.status === "green" ? DS.green : person.status === "amber" ? DS.amber : DS.danger,
            borderColor: person.status === "green" ? DS.green100 : person.status === "amber" ? DS.amber100 : DS.danger100,
          }}
        />
      </Box>

      <Box sx={{ color: DS.ink4, display: "grid", placeItems: "center" }}>
        <ChevronRightIcon sx={{ fontSize: 18 }} />
      </Box>
    </Box>
  );
}

function FilterBar({ tabs, activeFilter, onFilterChange, searchQuery, onSearchChange, showSearch = true }) {
  const DS = useDS();
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap", mb: 2.5 }}>
      <Box sx={{
        display: "flex", gap: "3px", bgcolor: "#fff", border: `1px solid ${DS.line}`, p: "4px",
        borderRadius: "11px", maxWidth: "100%",
        flexWrap: { xs: "wrap", sm: "nowrap" },
        overflowX: { xs: "visible", sm: "auto" },
      }}>
        {tabs.map((t) => (
          <Button
            key={t.id}
            onClick={() => onFilterChange(t.id)}
            size="small"
            sx={{
              fontSize: "12.5px", fontWeight: 600, color: activeFilter === t.id ? "#fff" : DS.ink3,
              bgcolor: activeFilter === t.id ? DS.primary : "transparent",
              borderRadius: "6px", textTransform: "none", px: "13px", py: "7px",
              minWidth: "auto", flexShrink: 0, whiteSpace: "nowrap",
              display: "inline-flex", alignItems: "center", gap: "7px",
              "&:hover": { color: activeFilter === t.id ? "#fff" : DS.ink, bgcolor: activeFilter === t.id ? DS.primary : "transparent" },
            }}
          >
            {t.label}
            <Box component="span" sx={{
              fontSize: "11px", fontWeight: 700, px: "7px", py: "1px", borderRadius: "999px",
              bgcolor: activeFilter === t.id ? "rgba(255,255,255,0.22)" : DS.line3,
              color: activeFilter === t.id ? "#fff" : DS.ink3,
              fontVariantNumeric: "tabular-nums",
            }}>
              {t.count}
            </Box>
          </Button>
        ))}
      </Box>
      <Box sx={{ flex: { xs: "1 1 100%", sm: 1 }, display: { xs: "none", sm: "block" } }} />
      {showSearch && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "9px", height: "38px", px: "13px", bgcolor: "#fff", border: `1px solid ${DS.line}`, borderRadius: "8px", minWidth: { xs: "100%", sm: 230 }, width: { xs: "100%", sm: "auto" } }}>
          <SearchIcon sx={{ fontSize: 18, color: DS.ink4, flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Search by name…"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            style={{ border: 0, outline: 0, font: "inherit", fontSize: "13px", color: DS.ink, background: "transparent", width: "100%" }}
          />
        </Box>
      )}
    </Box>
  );
}

function SectionLabel({ title, count }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mx: "2px", mb: "14px" }}>
      <Typography sx={{ fontSize: "11.5px", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: DS.ink3, whiteSpace: "nowrap", fontFamily: "var(--font-display)" }}>{title}</Typography>
      <Box sx={{ flex: 1, height: "1px", bgcolor: DS.line }} />
      {count != null && <Typography sx={{ fontSize: "12px", fontWeight: 600, color: DS.ink4 }}>{count}</Typography>}
    </Box>
  );
}


// ─── Certification Edit Dialog ──────────────────────────────────────────────
function CertEditDialog({ open, certType, existing, onSave, onClose }) {
  const DS = useDS();
  const blank = {
    certNumber: "",
    issueDate: "",
    expires: "",
    documentFile: null,
    documentName: "",
    evidenceUrl: "",
  };
  const [form, setForm] = useState(blank);
  const [uploadMode, setUploadMode] = useState("upload");
  const fileRef = React.useRef(null);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setForm({
        certNumber: existing.certNumber || "",
        issueDate: existing.issueDate || "",
        expires: existing.expires || "",
        documentFile: null,
        documentName: existing.documentName || "",
        evidenceUrl: existing.evidenceUrl || "",
      });
      setUploadMode(existing.evidenceUrl ? "url" : "upload");
    } else {
      setForm(blank);
      setUploadMode("upload");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existing]);

  function autoExpiry(issueDate) {
    if (!issueDate || !certType?.validityYears) return "";
    const d = new Date(issueDate);
    d.setFullYear(d.getFullYear() + certType.validityYears);
    return d.toISOString().slice(0, 10);
  }

  function handleIssueChange(val) {
    setForm((f) => ({
      ...f,
      issueDate: val,
      expires: f.expires || autoExpiry(val),
    }));
  }

  function handleFile(e) {
    const file = e.target.files[0];
    if (file)
      setForm((f) => ({ ...f, documentFile: file, documentName: file.name }));
  }

  function handleSave() {
    onSave({
      ...(existing || {}),
      certTypeId: certType.id,
      name: certType.name,
      issuer: certType.issuer,
      certNumber: form.certNumber,
      issueDate: form.issueDate,
      expires: form.expires,
      documentFile: form.documentFile,
      documentName: form.documentName,
      evidenceUrl: form.evidenceUrl,
    });
  }

  const canSave =
    form.certNumber.trim() &&
    form.issueDate &&
    (certType?.requiresExpiry ? form.expires : true) &&
    (form.documentFile || form.documentName || form.evidenceUrl.trim());

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px", bgcolor: DS.surfaceContainerLowest, boxShadow: "0px 8px 24px rgba(25, 28, 30, 0.12)" } }}
    >
      <DialogTitle sx={{ pb: 1, pt: 3, px: 3, fontWeight: 700, color: DS.primary, fontFamily: "var(--font-display)" }}>
        {existing ? "Edit Certification" : "Add Certification"}
      </DialogTitle>
      <DialogContent sx={{ px: 3 }}>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {certType && (
            <Paper elevation={0} sx={{ p: 2.5, backgroundColor: DS.surfaceContainerLow, borderRadius: "8px", border: "none" }}>
              <Typography variant="body2" color={DS.onSurfaceVariant} sx={{ mb: 0.5 }}>Certification Type</Typography>
              <Typography variant="subtitle1" fontWeight={700} color={DS.onSurface}>{certType.name}</Typography>
              <Typography variant="body2" color={DS.onSurfaceVariant}>
                Issuing Authority: <strong>{certType.issuer}</strong>
                {certType.validityYears && ` \u00b7 Valid for ${certType.validityYears} year(s)`}
              </Typography>
            </Paper>
          )}
          <TextField
            label="Certificate Number"
            value={form.certNumber}
            onChange={(e) => setForm((f) => ({ ...f, certNumber: e.target.value }))}
            fullWidth required placeholder="e.g., CERT-2024-001"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px", bgcolor: DS.surfaceContainerLow,
                "& fieldset": { borderColor: "transparent" },
                "&:hover fieldset": { borderColor: alpha(DS.primary, 0.25) },
                "&.Mui-focused fieldset": { borderColor: DS.primary, borderWidth: "2px" },
              },
            }}
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField type="date" label="Issue Date" value={form.issueDate} onChange={(e) => handleIssueChange(e.target.value)}
                fullWidth InputLabelProps={{ shrink: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px", bgcolor: DS.surfaceContainerLow,
                    "& fieldset": { borderColor: "transparent" },
                    "&:hover fieldset": { borderColor: alpha(DS.primary, 0.25) },
                    "&.Mui-focused fieldset": { borderColor: DS.primary, borderWidth: "2px" },
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              {certType?.requiresExpiry ? (
                <TextField type="date" label="Expiration Date" value={form.expires} onChange={(e) => setForm((f) => ({ ...f, expires: e.target.value }))}
                  fullWidth InputLabelProps={{ shrink: true }} required
                  helperText={certType?.validityYears ? "Auto-calculated from issue date" : ""}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px", bgcolor: DS.surfaceContainerLow,
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: alpha(DS.primary, 0.25) },
                      "&.Mui-focused fieldset": { borderColor: DS.primary, borderWidth: "2px" },
                    },
                  }}
                />
              ) : (
                <Box sx={{ p: 1.5, borderRadius: "8px", bgcolor: DS.surfaceContainerLow, border: `1px solid ${DS.outlineVariant}`, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "56px" }}>
                  <Chip label="Never expires" color="success" size="small" sx={{ fontWeight: 600, bgcolor: "rgba(25, 135, 84, 0.1)", color: "var(--color-success)" }} />
                </Box>
              )}
            </Grid>
          </Grid>
          <Box>
            <Typography variant="caption" fontWeight={700} color={DS.onSurfaceVariant} sx={{ textTransform: "uppercase", letterSpacing: "0.05em", display: "block", mb: 1.5 }}>
              Evidence Required *
            </Typography>
            <Stack spacing={1.5}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button size="small" variant={uploadMode === "upload" ? "contained" : "outlined"} onClick={() => setUploadMode("upload")} startIcon={<FileUploadIcon />}
                  sx={{ flex: 1, borderRadius: "8px", bgcolor: uploadMode === "upload" ? DS.primary : "transparent", color: uploadMode === "upload" ? "white" : DS.onSurfaceVariant }}>
                  Upload
                </Button>
                <Button size="small" variant={uploadMode === "url" ? "contained" : "outlined"} onClick={() => setUploadMode("url")} startIcon={<LinkIcon />}
                  sx={{ flex: 1, borderRadius: "8px", bgcolor: uploadMode === "url" ? DS.primary : "transparent", color: uploadMode === "url" ? "white" : DS.onSurfaceVariant }}>
                  Link
                </Button>
              </Box>
              {uploadMode === "upload" && (
                <Box>
                  <input ref={fileRef} type="file" hidden accept="image/*,.pdf" onChange={handleFile} />
                  <Button variant="outlined" startIcon={<FileUploadIcon />} onClick={() => fileRef.current.click()} fullWidth
                    sx={{
                      py: 1.5, justifyContent: "flex-start", borderRadius: "8px",
                      borderColor: form.documentName ? DS.primary : DS.outlineVariant,
                      color: form.documentName ? DS.primary : DS.onSurfaceVariant,
                      bgcolor: form.documentName ? alpha(DS.primary, 0.04) : "transparent",
                      "&:hover": { borderColor: DS.primary, bgcolor: alpha(DS.primary, 0.04) },
                    }}>
                    {form.documentName ? `\u2713 ${form.documentName}` : "Select PDF or Image"}
                  </Button>
                  <Typography variant="caption" color={DS.onSurfaceVariant} sx={{ mt: 0.5, display: "block" }}>PDF or image file (max 10MB)</Typography>
                </Box>
              )}
              {uploadMode === "url" && (
                <TextField label="Certificate URL" placeholder="https://example.com/certificate" value={form.evidenceUrl} onChange={(e) => setForm((f) => ({ ...f, evidenceUrl: e.target.value }))}
                  fullWidth size="small" type="url" helperText="Must be a public link to your certification"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px", bgcolor: DS.surfaceContainerLow,
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: alpha(DS.primary, 0.25) },
                      "&.Mui-focused fieldset": { borderColor: DS.primary, borderWidth: "2px" },
                    },
                  }}
                />
              )}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button onClick={onClose} variant="outlined" size="large" sx={{ borderRadius: "8px" }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave} size="large" sx={{ borderRadius: "8px", bgcolor: DS.primary, backgroundImage: "none", "&:hover": { bgcolor: DS.primaryContainer } }}>
          {existing ? "Save Changes" : "Add Certification"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Certification Details Dialog ───────────────────────────────────────────
export function CertDetailsDialog({ open, certType, cert, nextClass, onSave, onDelete, onClose }) {
  const DS = useDS();
  const navigate = useNavigate();
  const fileRef = React.useRef(null);
  const [form, setForm] = useState({ certNumber: "", issueDate: "", expires: "", documentFile: null, documentName: "", evidenceUrl: "" });
  const [uploadMode, setUploadMode] = useState("upload");
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open || !cert) return;
    setForm({
      certNumber: cert.certNumber || "",
      issueDate: cert.issueDate || "",
      expires: cert.expires || "",
      documentFile: null,
      documentName: cert.documentName || "",
      evidenceUrl: cert.evidenceUrl || "",
    });
    setUploadMode(cert.evidenceUrl && !cert.evidenceUrl.startsWith("/api/") ? "url" : "upload");
    setConfirmDelete(false);
  }, [open, cert]);

  function autoExpiry(issueDate) {
    if (!issueDate || !certType?.validityYears) return "";
    const d = new Date(issueDate);
    d.setFullYear(d.getFullYear() + certType.validityYears);
    return d.toISOString().slice(0, 10);
  }

  function handleIssueChange(val) {
    setForm((f) => ({ ...f, issueDate: val, expires: f.expires || autoExpiry(val) }));
  }

  function handleFile(e) {
    const file = e.target.files[0];
    if (file) setForm((f) => ({ ...f, documentFile: file, documentName: file.name }));
  }

  function handleSave() {
    onSave({
      ...cert,
      certTypeId: certType.id,
      name: certType.name,
      issuer: certType.issuer,
      certNumber: form.certNumber,
      issueDate: form.issueDate,
      expires: form.expires,
      documentFile: form.documentFile,
      documentName: form.documentName,
      evidenceUrl: form.evidenceUrl,
    });
  }

  if (!cert || !certType) return null;

  const status = certStatus(cert);
  const { dsBg, dsText, label: statusLabel } = STATUS_CFG[status];
  const days = cert ? daysUntil(cert.expires) : null;
  const canSave = form.certNumber.trim() && form.issueDate && (certType?.requiresExpiry ? form.expires : true);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: "12px", bgcolor: DS.surfaceContainerLowest, boxShadow: "0px 8px 24px rgba(25, 28, 30, 0.12)" } }}>
      <DialogTitle sx={{ px: 3, pt: 3, pb: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 0.25, color: DS.primary, fontFamily: "var(--font-display)" }}>{certType.name}</Typography>
            <Typography variant="caption" color={DS.onSurfaceVariant}>
              {certType.issuer}{certType.validityYears && ` \u00b7 Valid for ${certType.validityYears} year(s)`}
            </Typography>
          </Box>
          <Chip label={statusLabel} size="small" sx={{ bgcolor: dsBg, color: dsText, fontWeight: 700, flexShrink: 0, borderRadius: "9999px", px: 1 }} />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3 }}>
        <Stack spacing={2.5}>
          {days !== null && (
            <Box sx={{ px: 2.5, py: 1.5, borderRadius: "8px", backgroundColor: dsBg, border: `1px solid ${STATUS_CFG[status].dsBorder}` }}>
              <Typography variant="body2" fontWeight={600} color={dsText}>
                {days > 0 ? `${days} day${days !== 1 ? "s" : ""} until expiration` : days === 0 ? "Expires today" : `Expired ${Math.abs(days)} day${Math.abs(days) !== 1 ? "s" : ""} ago`}
              </Typography>
            </Box>
          )}

          <TextField label="Certificate Number" value={form.certNumber} onChange={(e) => setForm((f) => ({ ...f, certNumber: e.target.value }))}
            fullWidth required size="small" placeholder="e.g., CERT-2024-001"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px", bgcolor: DS.surfaceContainerLow,
                "& fieldset": { borderColor: "transparent" },
                "&:hover fieldset": { borderColor: alpha(DS.primary, 0.25) },
                "&.Mui-focused fieldset": { borderColor: DS.primary, borderWidth: "2px" },
              },
            }}
          />

          <Stack direction="row" spacing={2}>
            <TextField type="date" label="Issue Date" value={form.issueDate} onChange={(e) => handleIssueChange(e.target.value)}
              fullWidth size="small" InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px", bgcolor: DS.surfaceContainerLow,
                  "& fieldset": { borderColor: "transparent" },
                  "&:hover fieldset": { borderColor: alpha(DS.primary, 0.25) },
                  "&.Mui-focused fieldset": { borderColor: DS.primary, borderWidth: "2px" },
                },
              }}
            />
            {certType?.requiresExpiry ? (
              <TextField type="date" label="Expiration Date" value={form.expires} onChange={(e) => setForm((f) => ({ ...f, expires: e.target.value }))}
                fullWidth size="small" InputLabelProps={{ shrink: true }}
                helperText={certType?.validityYears ? "Auto-calculated from issue date" : ""}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px", bgcolor: DS.surfaceContainerLow,
                    "& fieldset": { borderColor: "transparent" },
                    "&:hover fieldset": { borderColor: alpha(DS.primary, 0.25) },
                    "&.Mui-focused fieldset": { borderColor: DS.primary, borderWidth: "2px" },
                  },
                }}
              />
            ) : (
              <Box sx={{ p: 1.5, borderRadius: "8px", bgcolor: DS.surfaceContainerLow, border: `1px solid ${DS.outlineVariant}`, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "56px", width: "100%" }}>
                <Chip label="Never expires" color="success" size="small" sx={{ fontWeight: 600, bgcolor: "rgba(25, 135, 84, 0.1)", color: "var(--color-success)" }} />
              </Box>
            )}
          </Stack>

          {(form.documentName || form.evidenceUrl) && (
            <Paper elevation={0} sx={{ p: 2, bgcolor: DS.surfaceContainerLow, borderRadius: "8px", border: `1px solid ${DS.outlineVariant}` }}>
              <Typography variant="caption" fontWeight={700} color={DS.onSurfaceVariant} sx={{ textTransform: "uppercase", letterSpacing: "0.05em", display: "block", mb: 1 }}>
                Current Evidence
              </Typography>
              {form.documentName && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <FileUploadIcon sx={{ fontSize: 16, color: DS.primary }} />
                  <Typography variant="body2" color={DS.onSurface} sx={{ flex: 1, wordBreak: "break-all" }}>{form.documentName}</Typography>
                </Box>
              )}
              {form.evidenceUrl && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Button size="small" variant="outlined" startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />} onClick={() => openEvidenceDocument(form.evidenceUrl)}
                    sx={{ borderRadius: "8px", borderColor: DS.outlineVariant, color: DS.onSurface, textTransform: "none", fontWeight: 600, "&:hover": { borderColor: DS.primary, bgcolor: "rgba(0,56,108,0.04)" } }}>
                    View Document
                  </Button>
                </Box>
              )}
            </Paper>
          )}

          <Box>
            <Typography variant="caption" fontWeight={700} color={DS.onSurfaceVariant} sx={{ textTransform: "uppercase", letterSpacing: "0.05em", display: "block", mb: 1.5 }}>
              Update Evidence
            </Typography>
            <Stack spacing={1.5}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button size="small" variant={uploadMode === "upload" ? "contained" : "outlined"} onClick={() => setUploadMode("upload")} startIcon={<FileUploadIcon />}
                  sx={{ flex: 1, borderRadius: "8px", bgcolor: uploadMode === "upload" ? DS.primary : "transparent", color: uploadMode === "upload" ? "white" : DS.onSurfaceVariant }}>
                  Upload
                </Button>
                <Button size="small" variant={uploadMode === "url" ? "contained" : "outlined"} onClick={() => setUploadMode("url")} startIcon={<LinkIcon />}
                  sx={{ flex: 1, borderRadius: "8px", bgcolor: uploadMode === "url" ? DS.primary : "transparent", color: uploadMode === "url" ? "white" : DS.onSurfaceVariant }}>
                  Link
                </Button>
              </Box>
              {uploadMode === "upload" && (
                <Box>
                  <input ref={fileRef} type="file" hidden accept="image/*,.pdf" onChange={handleFile} />
                  <Button variant="outlined" startIcon={<FileUploadIcon />} onClick={() => fileRef.current.click()} fullWidth size="small"
                    sx={{
                      py: 1, justifyContent: "flex-start", borderRadius: "8px",
                      borderColor: form.documentFile ? DS.primary : DS.outlineVariant,
                      color: form.documentFile ? DS.primary : DS.onSurfaceVariant,
                      bgcolor: form.documentFile ? alpha(DS.primary, 0.04) : "transparent",
                      "&:hover": { borderColor: DS.primary, bgcolor: alpha(DS.primary, 0.04) },
                    }}>
                    {form.documentFile ? `\u2713 ${form.documentFile.name}` : "Select new file to replace"}
                  </Button>
                  <Typography variant="caption" color={DS.onSurfaceVariant} sx={{ mt: 0.5, display: "block" }}>Leave empty to keep current file</Typography>
                </Box>
              )}
              {uploadMode === "url" && (
                <TextField fullWidth label="Certificate URL" placeholder="https://example.com/certificate" value={form.evidenceUrl} onChange={(e) => setForm((f) => ({ ...f, evidenceUrl: e.target.value }))}
                  size="small" type="url" helperText="Must be a public link to your certification"
                  sx={{
                    bgcolor: DS.surfaceContainerLow, borderRadius: "8px",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px", bgcolor: DS.surfaceContainerLow,
                      "& fieldset": { borderColor: DS.outlineVariant },
                      "&:hover fieldset": { borderColor: DS.outline },
                      "&.Mui-focused fieldset": { borderColor: DS.primary, borderWidth: "2px" },
                    },
                  }}
                />
              )}
            </Stack>
          </Box>

          {nextClass && (
            <Box>
              <Typography variant="caption" color={DS.onSurfaceVariant} fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: "0.05em", display: "block", mb: 1 }}>
                Available for Renewal
              </Typography>
              <Paper elevation={0} onClick={() => { onClose(); navigate(`/classes/${nextClass.id}`); }}
                sx={{
                  p: 2.5, bgcolor: DS.surfaceContainerLow, borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", gap: 1.5,
                  transition: "all 0.2s ease", "&:hover": { bgcolor: DS.surfaceContainerHigh, boxShadow: "0px 4px 12px rgba(25, 28, 30, 0.08)" },
                }}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700} color={DS.onSurface}>{nextClass.title}</Typography>
                  <Typography variant="caption" color={DS.onSurfaceVariant}>{fmtDate(nextClass.date)} \u00b7 {nextClass.availableSeats} seats available</Typography>
                </Box>
                <ArrowForwardIcon sx={{ fontSize: 16, color: DS.primary, flexShrink: 0 }} />
              </Paper>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
        {confirmDelete ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" color={STATUS_CFG.expired.dsText}>Remove this record?</Typography>
            <Button size="small" variant="contained" color="error" onClick={onDelete} sx={{ borderRadius: "8px" }}>Remove</Button>
            <Button size="small" onClick={() => setConfirmDelete(false)} sx={{ borderRadius: "8px" }}>Cancel</Button>
          </Box>
        ) : (
          <Button color="error" size="small" onClick={() => setConfirmDelete(true)} sx={{ borderRadius: "8px" }}>Remove Record</Button>
        )}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose} variant="outlined" size="small" sx={{ borderRadius: "8px" }}>Cancel</Button>
          <Button variant="contained" size="small" onClick={handleSave} disabled={!canSave} sx={{ borderRadius: "8px", bgcolor: DS.primary, backgroundImage: "none", "&:hover": { bgcolor: DS.primaryContainer } }}>
            Save Changes
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

// ─── Upload New Certification Dialog ────────────────────────────────────────
function formatBytes(bytes) {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

function UploadCertDialog({ open, allowedTypes, onSave, onClose }) {
  const DS = useDS();
  const blank = { certTypeId: "", issuer: "", issueDate: "", expires: "", file: null, fileName: "", evidenceUrl: "" };
  const [form, setForm] = useState(blank);
  const [dragging, setDragging] = useState(false);
  const [uploadMode, setUploadMode] = useState("upload");
  const fileRef = React.useRef(null);

  useEffect(() => {
    if (!open) {
      setForm(blank);
      setUploadMode("upload");
    }
    // eslint-disable-next-line
  }, [open]);

  function handleFile(file) {
    if (!file) return;
    setForm((f) => ({ ...f, file, fileName: file.name }));
  }

  function handleRemoveFile() {
    setForm((f) => ({ ...f, file: null, fileName: "" }));
    if (fileRef.current) fileRef.current.value = "";
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  const selectedCertType = allowedTypes.find((t) => t.id === form.certTypeId);

  function autoExpiry(issueDate) {
    if (!issueDate || !selectedCertType?.validityYears) return "";
    const d = new Date(issueDate);
    d.setFullYear(d.getFullYear() + selectedCertType.validityYears);
    return d.toISOString().slice(0, 10);
  }

  function handleIssueDateChange(value) {
    setForm((f) => ({ ...f, issueDate: value, expires: f.expires || autoExpiry(value) }));
  }

  function handleSave() {
    const certType = selectedCertType;
    onSave({
      certTypeId: form.certTypeId,
      name: certType?.name || "",
      issuer: form.issuer || certType?.issuer || "",
      issueDate: form.issueDate,
      expires: form.expires || (certType?.validityYears ? autoExpiry(form.issueDate) : undefined),
      documentName: form.fileName,
      documentFile: form.file,
      evidenceUrl: form.evidenceUrl,
    });
  }

  const canSave = allowedTypes.length > 0 && form.certTypeId && form.issueDate && (!selectedCertType?.requiresExpiry || form.expires) && (form.file || form.fileName || form.evidenceUrl.trim());

  const labelStyle = { fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: DS.onSurfaceVariant, mb: 0.75, display: "block" };

  const inputSx = {
    bgcolor: "#fff", borderRadius: "10px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px", bgcolor: "#fff",
      "& fieldset": { borderColor: DS.outlineVariant },
      "&:hover fieldset": { borderColor: DS.outline },
      "&.Mui-focused fieldset": { borderColor: DS.primary, borderWidth: "2px" },
    },
    "& .MuiInputBase-input": { fontSize: "0.95rem", py: 1.3 },
  };

  const dateInputSx = {
    ...inputSx,
    "& .MuiInputBase-input": { py: 1.3, pl: 0.5 },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false}
      PaperProps={{ sx: { width: "100%", maxWidth: "508px", borderRadius: "22px", boxShadow: "0 24px 80px rgba(0,0,0,0.16)", overflow: "hidden", bgcolor: "#fff" } }}>
      {/* Header */}
      <Box sx={{ px: 3.5, pt: 3.5, pb: 2.5, textAlign: "center", position: "relative" }}>
        <Typography variant="h5" fontWeight={800} color={DS.primary} sx={{ fontSize: "1.35rem", lineHeight: 1.2, fontFamily: "var(--font-display)" }}>Upload New Certification</Typography>
        <Typography variant="body2" color={DS.onSurfaceVariant} sx={{ mt: 0.75, maxWidth: 320, mx: "auto" }}>Add a digital copy of your professional credential to your profile.</Typography>
        <IconButton onClick={onClose} size="small" sx={{ position: "absolute", top: 16, right: 16, color: DS.onSurfaceVariant, width: 36, height: 36, border: `1px solid ${DS.outlineVariant}`, borderRadius: "10px", bgcolor: "#fff", "&:hover": { bgcolor: DS.surfaceContainerLow } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3.5, pt: 0, pb: 0 }}>
        <Stack spacing={2.75}>
          {/* Certification Name */}
          <Box>
            <Typography sx={labelStyle}>Certification Name</Typography>
            <Select fullWidth displayEmpty value={form.certTypeId} onChange={(e) => setForm((f) => ({ ...f, certTypeId: e.target.value }))}
              sx={{ borderRadius: "10px", bgcolor: "#fff", "& .MuiSelect-select": { py: 1.3, fontSize: "0.95rem" }, "& fieldset": { borderColor: DS.outlineVariant }, "&:hover fieldset": { borderColor: DS.outline }, "&.Mui-focused fieldset": { borderColor: DS.primary, borderWidth: "2px" } }}>
              <MenuItem value="" disabled><Typography color={DS.outline} sx={{ fontSize: "0.95rem" }}>Select a certification</Typography></MenuItem>
              {allowedTypes.map((t) => (<MenuItem key={t.id} value={t.id} sx={{ fontSize: "0.95rem" }}>{t.name}</MenuItem>))}
            </Select>
            {allowedTypes.length === 0 && (
              <Typography variant="caption" color={DS.onSurfaceVariant} sx={{ mt: 1, display: "block" }}>All available certification types are already on file.</Typography>
            )}
          </Box>

          {/* Issuing Organization */}
          <Box>
            <Typography sx={labelStyle}>Issuing Organization</Typography>
            <TextField fullWidth placeholder="e.g. Lumen Global Academy" value={form.issuer} onChange={(e) => setForm((f) => ({ ...f, issuer: e.target.value }))} sx={inputSx} />
          </Box>

          {/* Dates */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={labelStyle}>Issue Date</Typography>
              <TextField type="date" fullWidth value={form.issueDate} onChange={(e) => handleIssueDateChange(e.target.value)} InputLabelProps={{ shrink: true }}
                InputProps={{ startAdornment: <CalendarMonthIcon sx={{ color: DS.outline, mr: 0.75, fontSize: 20 }} /> }}
                sx={dateInputSx} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={labelStyle}>Expiration Date</Typography>
              {selectedCertType ? (
                selectedCertType.requiresExpiry ? (
                  <TextField type="date" fullWidth value={form.expires} onChange={(e) => setForm((f) => ({ ...f, expires: e.target.value }))} InputLabelProps={{ shrink: true }}
                    InputProps={{ startAdornment: <CalendarMonthIcon sx={{ color: DS.outline, mr: 0.75, fontSize: 20 }} /> }}
                    helperText={selectedCertType?.validityYears ? "Auto-calculated from issue date" : ""} sx={dateInputSx} />
                ) : (
                  <Box sx={{ px: 2, py: 1.3, borderRadius: "10px", bgcolor: "#fff", border: `1px solid ${DS.outlineVariant}`, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "48px" }}>
                    <Chip label="Never expires" color="success" size="small" sx={{ fontWeight: 600, bgcolor: "rgba(25, 135, 84, 0.1)", color: "var(--color-success)" }} />
                  </Box>
                )
              ) : (
                <TextField type="date" fullWidth value={form.expires} onChange={(e) => setForm((f) => ({ ...f, expires: e.target.value }))} InputLabelProps={{ shrink: true }}
                  InputProps={{ startAdornment: <CalendarMonthIcon sx={{ color: DS.outline, mr: 0.75, fontSize: 20 }} /> }}
                  sx={dateInputSx} />
              )}
            </Box>
          </Box>

          {/* Evidence */}
          <Box>
            <Typography sx={labelStyle}>Evidence</Typography>
            {/* Segmented toggle */}
            <Box sx={{
              display: "flex", gap: 0.5, p: 0.5, borderRadius: "10px", bgcolor: "#fff", border: `1px solid ${DS.outlineVariant}`, mb: 2,
            }}>
              <Button size="small" onClick={() => setUploadMode("upload")} startIcon={<FileUploadIcon sx={{ fontSize: 18 }} />}
                sx={{
                  flex: 1, borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: "0.85rem",
                  bgcolor: uploadMode === "upload" ? "#fff" : "transparent",
                  color: uploadMode === "upload" ? DS.primary : DS.onSurfaceVariant,
                  boxShadow: uploadMode === "upload" ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                  border: uploadMode === "upload" ? `1px solid ${DS.outlineVariant}` : "1px solid transparent",
                  "&:hover": { bgcolor: uploadMode === "upload" ? "#fff" : "rgba(0,0,0,0.03)" },
                }}>Upload File</Button>
              <Button size="small" onClick={() => setUploadMode("url")} startIcon={<LinkIcon sx={{ fontSize: 18 }} />}
                sx={{
                  flex: 1, borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: "0.85rem",
                  bgcolor: uploadMode === "url" ? "#fff" : "transparent",
                  color: uploadMode === "url" ? DS.primary : DS.onSurfaceVariant,
                  boxShadow: uploadMode === "url" ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                  border: uploadMode === "url" ? `1px solid ${DS.outlineVariant}` : "1px solid transparent",
                  "&:hover": { bgcolor: uploadMode === "url" ? "#fff" : "rgba(0,0,0,0.03)" },
                }}>Add Link</Button>
            </Box>

            {uploadMode === "upload" && (
              <>
                {form.fileName ? (
                  <Box sx={{
                    display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.5, borderRadius: "12px", border: `1px solid ${DS.outlineVariant}`, bgcolor: "#fff",
                  }}>
                    <InsertDriveFileIcon sx={{ color: DS.primary, fontSize: 28 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} color={DS.onSurface} sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{form.fileName}</Typography>
                      <Typography variant="caption" color={DS.onSurfaceVariant}>{formatBytes(form.file?.size)}</Typography>
                    </Box>
                    <IconButton size="small" onClick={handleRemoveFile} sx={{ color: DS.onSurfaceVariant, "&:hover": { color: DS.danger, bgcolor: DS.danger50 } }}>
                      <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <Box onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={handleDrop} onClick={() => fileRef.current.click()}
                    sx={{
                      border: `2px dashed ${dragging ? DS.primary : DS.outlineVariant}`, borderRadius: "14px",
                      bgcolor: dragging ? alpha(DS.primary, 0.04) : "#fff", py: 3.5, px: 2,
                      display: "flex", flexDirection: "column", alignItems: "center", gap: 1.25, cursor: "pointer", transition: "all 0.15s ease",
                      "&:hover": { border: `2px dashed ${DS.outline}`, bgcolor: "rgba(0,56,108,0.02)" },
                    }}>
                    <input ref={fileRef} type="file" hidden accept="image/*,.pdf" onChange={(e) => handleFile(e.target.files[0])} />
                    <Box sx={{ width: 44, height: 44, borderRadius: "50%", bgcolor: DS.primary10, display: "flex", alignItems: "center", justifyContent: "center", mb: 0.25 }}>
                      <UploadFileIcon sx={{ fontSize: 22, color: DS.primary }} />
                    </Box>
                    <Typography variant="body2" color={DS.onSurface} sx={{ textAlign: "center" }}>
                      <Box component="span" sx={{ fontWeight: 700, color: DS.primary }}>Click to upload</Box>{" "}or drag and drop
                    </Typography>
                    <Typography variant="caption" color={DS.outline} sx={{ textAlign: "center" }}>PDF, JPG, PNG up to 10MB</Typography>
                  </Box>
                )}
              </>
            )}

            {uploadMode === "url" && (
              <TextField fullWidth placeholder="https://example.com/certificate" value={form.evidenceUrl} onChange={(e) => setForm((f) => ({ ...f, evidenceUrl: e.target.value }))}
                type="url"
                InputProps={{ startAdornment: <LinkIcon sx={{ color: DS.outline, mr: 0.75, fontSize: 20 }} /> }}
                helperText="Must be a public link to your certification"
                sx={inputSx} />
            )}
          </Box>
        </Stack>
      </DialogContent>

      {/* Footer */}
      <Box sx={{ px: 3.5, py: 3, mt: 2.5, borderTop: `1px solid ${DS.surfaceContainerHigh}`, bgcolor: "#fff", display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
        <Button onClick={onClose} variant="outlined" sx={{ textTransform: "none", color: DS.onSurfaceVariant, fontWeight: 600, px: 3, borderRadius: "10px", borderColor: DS.outlineVariant, "&:hover": { borderColor: DS.outline, bgcolor: "rgba(0,0,0,0.02)" } }}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!canSave} onClick={handleSave} endIcon={<ArrowForwardIcon />}
          sx={{
            bgcolor: DS.primary, backgroundImage: "none", color: "#fff", fontWeight: 700, textTransform: "none", borderRadius: "10px", px: 3,
            "&:hover": { bgcolor: DS.primary700 },
            "&.Mui-disabled": { bgcolor: alpha(DS.primary, 0.12), backgroundImage: "none", color: alpha(DS.primary, 0.38) },
          }}>
          Upload Certification
        </Button>
      </Box>
    </Dialog>
  );
}


// ─── Student View ────────────────────────────────────────────────────────────
function StudentView({ userId, tenantId, isInstructor = false }) {
  const DS = useDS();
  const [myCerts, setMyCerts] = useState([]);
  const [requiredTypes, setRequiredTypes] = useState([]);
  // Cert type ids required by the user's agency roles; only these show as "missing" placeholders.
  const [requiredIds, setRequiredIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scope, setScope] = useState("earned");
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Dialog states
  const [editOpen, setEditOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedCertType, setSelectedCertType] = useState(null);
  const [selectedCert, setSelectedCert] = useState(null);
  const [nextClasses, setNextClasses] = useState({});
  const [completions, setCompletions] = useState([]);
  const [loaded, setLoaded] = useState(false);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);
      const [certs, types, memberships] = await Promise.all([
        fetchUserCertifications(userId),
        fetchAllowedCertifications(tenantId),
        fetchUserMemberships(userId).catch(() => []),
      ]);
      setRequiredIds(new Set(
        memberships
          .filter((m) => m.status === "APPROVED" && String(m.tenantId) === String(tenantId))
          .flatMap((m) => (Array.isArray(m.requiredCertIds) ? m.requiredCertIds : []))
          .map(String),
      ));
      // Class completion certificates are proof of completion (transcript), not tracked certifications.
      setMyCerts(certs.filter((c) => c.source !== "COURSE_COMPLETION"));
      setRequiredTypes(types);
      if (!isInstructor) {
        // Completion certificates only feed the timeline pins; a failure must not hide the tracked certifications.
        try {
          const done = await fetchCompletedCourses(userId);
          setCompletions(Array.isArray(done) ? done : []);
        } catch {
          setCompletions([]);
        }
      }
      if (types.length > 0) {
        const nextMap = await fetchNextClassesForAllowedCerts(types.map((t) => t.id));
        setNextClasses(nextMap);
      }
    } catch (e) {
      setError(e.message || "Failed to load certifications");
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [userId, tenantId]);

  // Build cert list from required types + any extra certs
  const allCerts = useMemo(() => {
    const combined = [];
    const typeIds = new Set();
    requiredTypes.forEach((t) => {
      const cert = findCertForType(myCerts, t);
      if (!cert && !requiredIds.has(String(t.id))) return;
      typeIds.add(t.id);
      combined.push({ certType: t, cert });
    });
    myCerts.forEach((c) => {
      if (!typeIds.has(c.certTypeId)) {
        combined.push({ certType: null, cert: c });
      }
    });
    return combined;
  }, [myCerts, requiredTypes, requiredIds]);

  const activeCount = allCerts.filter((c) => certStatus(c.cert) === "active").length;
  const expiringCount = allCerts.filter((c) => certStatus(c.cert) === "expiring").length;
  const expiredCount = allCerts.filter((c) => certStatus(c.cert) === "expired").length;
  const missingCount = allCerts.filter((c) => certStatus(c.cert) === "missing").length;

  // Required certification types without a record are actionable placeholders,
  // not earned certificates. Keep them out of the normal earned results so the
  // summary, All filter, and section count agree; reveal them only via Missing.
  const earnedCerts = useMemo(
    () => allCerts.filter((item) => Boolean(item.cert)),
    [allCerts],
  );
  const renewalCerts = useMemo(
    () => earnedCerts.filter((item) => {
      const status = certStatus(item.cert);
      return status === "expiring" || status === "expired";
    }),
    [earnedCerts],
  );

  const filteredCerts = useMemo(() => {
    let items;
    if (scope === "renewals") {
      items = renewalCerts;
    } else if (filter === "missing") {
      items = allCerts.filter((item) => certStatus(item.cert) === "missing");
    } else {
      items = earnedCerts;
    }
    if (filter !== "all") {
      items = items.filter((c) => certStatus(c.cert) === filter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter((c) =>
        (c.certType?.name || c.cert?.name || "").toLowerCase().includes(q) ||
        (c.certType?.issuer || c.cert?.issuer || "").toLowerCase().includes(q)
      );
    }
    return items;
  }, [allCerts, earnedCerts, renewalCerts, scope, filter, searchQuery]);

  function openUpload(certType) {
    setSelectedCertType(certType);
    setSelectedCert(null);
    setEditOpen(false);
    setDetailsOpen(false);
    setUploadOpen(true);
  }

  function openDetails(certType, cert) {
    setSelectedCertType(certType);
    setSelectedCert(cert);
    setEditOpen(false);
    setUploadOpen(false);
    setDetailsOpen(true);
  }

  async function handleSaveCert(certData) {
    const formData = new FormData();
    formData.append("name", certData.name || "");
    formData.append("issuer", certData.issuer || "");
    formData.append("certNumber", certData.certNumber || "");
    if (certData.certTypeId) formData.append("certTypeId", certData.certTypeId);
    if (certData.issueDate) formData.append("issueDate", certData.issueDate);
    if (certData.expires) formData.append("expires", certData.expires);
    if (certData.documentFile) {
      formData.append("document", certData.documentFile);
      if (certData.documentName) formData.append("documentName", certData.documentName);
    }
    if (certData.evidenceUrl) formData.append("evidenceUrl", certData.evidenceUrl);

    try {
      if (certData.id) {
        await updateCertification(userId, certData.id, formData);
      } else {
        await createCertification(userId, formData);
      }
      setEditOpen(false);
      setUploadOpen(false);
      setDetailsOpen(false);
      await loadData();
    } catch (e) {
      setError(e.message || "Failed to save certification");
    }
  }

  async function handleDeleteCert() {
    if (!selectedCert?.id) return;
    try {
      await deleteCertification(userId, selectedCert.id);
      setDetailsOpen(false);
      setEditOpen(false);
      await loadData();
    } catch (e) {
      setError(e.message || "Failed to delete certification");
    }
  }

  // Scope tabs
  const scopes = isInstructor
    ? [
        { id: "earned", label: "My Certifications", icon: <VerifiedIcon sx={{ fontSize: 16 }} /> },
        { id: "renewals", label: "Renewals", icon: <RefreshIcon sx={{ fontSize: 16 }} /> },
      ]
    : [
        { id: "earned", label: "Earned", icon: <VerifiedIcon sx={{ fontSize: 16 }} /> },
        { id: "renewals", label: "Renewals", icon: <RefreshIcon sx={{ fontSize: 16 }} /> },
      ];

  // Filter tabs
  const filterableCerts = scope === "renewals" ? renewalCerts : earnedCerts;
  const filterTabs = [
    { id: "all", label: "All", count: filterableCerts.length },
    { id: "active", label: "Active", count: filterableCerts.filter((c) => certStatus(c.cert) === "active").length },
    { id: "expiring", label: "Expiring", count: filterableCerts.filter((c) => certStatus(c.cert) === "expiring").length },
    { id: "expired", label: "Expired", count: filterableCerts.filter((c) => certStatus(c.cert) === "expired").length },
    ...(scope !== "renewals" ? [{ id: "missing", label: "Missing", count: missingCount }] : []),
  ];

  async function handleDownloadDocument(cert) {
    await openEvidenceDocument(cert?.evidenceUrl);
  }

  async function handleDownloadCompletion(pin) {
    try {
      const blob = await downloadCertificatePdf(pin.certificateId);
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `certificate-${pin.certNumber || pin.certificateId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      setError(e.message || "Failed to download the certificate PDF");
    }
  }

  async function handleCopyLink(url) {
    await navigator.clipboard.writeText(url);
  }

  const dialogs = (
    <>
      <CertEditDialog
        open={editOpen}
        certType={selectedCertType}
        existing={selectedCert}
        onSave={handleSaveCert}
        onClose={() => { setEditOpen(false); setSelectedCert(null); }}
      />
      <CertDetailsDialog
        open={detailsOpen}
        certType={selectedCertType}
        cert={selectedCert}
        nextClass={selectedCertType ? nextClasses[selectedCertType.id] : null}
        onSave={handleSaveCert}
        onDelete={handleDeleteCert}
        onClose={() => { setDetailsOpen(false); setSelectedCert(null); }}
      />
      <UploadCertDialog
        open={uploadOpen}
        allowedTypes={requiredTypes.filter((t) => {
          const c = findCertForType(myCerts, t);
          return !c || certStatus(c) !== "active";
        })}
        onSave={handleSaveCert}
        onClose={() => setUploadOpen(false)}
      />
    </>
  );

  if (!isInstructor && loaded) {
    return (
      <StudentCertificationsView
        certs={myCerts}
        requiredTypes={requiredTypes}
        missingTypeIds={requiredIds}
        completions={completions}
        error={error}
        onDismissError={() => setError(null)}
        onUpload={(certType) => openUpload(certType || requiredTypes[0] || null)}
        onOpenDetails={openDetails}
        onDownloadDocument={handleDownloadDocument}
        onDownloadCompletion={handleDownloadCompletion}
        onCopyLink={handleCopyLink}
      >
        {dialogs}
      </StudentCertificationsView>
    );
  }

  const pageTitle = isInstructor ? "Instructor Certifications" : "My Certifications";

  if (loading && !(loaded && !isInstructor)) {
    return (
      <Box sx={{ maxWidth: 1100, mx: "auto" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "13px", mb: 3 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: "11px", bgcolor: "#eee" }} />
          <Box>
            <Box sx={{ width: 200, height: 18, bgcolor: "#eee", borderRadius: 1, mb: 0.5 }} />
            <Box sx={{ width: 160, height: 14, bgcolor: "#eee", borderRadius: 1 }} />
          </Box>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "18px" }}>
          {[1, 2, 3].map((i) => (
            <Box key={i} sx={{ height: 280, borderRadius: "15px", bgcolor: "#eee", animation: "pulse 1.5s infinite" }} />
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <CertificationShell
      title={pageTitle}
      description={isInstructor ? "Track your teaching credentials and training certifications." : "Your credentials and eCards. Add your own certifications and renew them before they expire. Class completion certificates are on your transcript."}
      action={<Button
          variant="contained"
          startIcon={<FileUploadIcon sx={{ fontSize: 18 }} />}
          onClick={() => openUpload(requiredTypes[0] || null)}
          sx={{
            height: 40, borderRadius: "11px", textTransform: "none", fontWeight: 700, fontSize: "13.5px",
            px: "18px", bgcolor: DS.primary, backgroundImage: "none", "&:hover": { bgcolor: DS.primary700 },
            boxShadow: "0 1px 0 rgba(20,24,40,.04), 0 1px 3px rgba(20,24,40,.06)",
          }}
        >
          Upload
        </Button>}
      tabs={scopes.map((item) => ({ id: item.id, label: item.label, count: item.id === "renewals" ? expiringCount + expiredCount : activeCount + expiringCount + expiredCount }))}
      activeTab={scope}
      onTabChange={(nextScope) => { setScope(nextScope); setFilter("all"); }}
    >

      {scope === "earned" && <AhaEcardsSection />}

      {missingCount > 0 && scope === "earned" && (
        <Banner
          tone="danger"
          icon="alert"
          text={`You have <b>${missingCount} required certification${missingCount !== 1 ? "s" : ""}</b> not on file. Upload before your next assigned class.`}
          cta="Upload Missing"
          onCta={() => { setScope("earned"); setFilter("missing"); }}
        />
      )}

      {expiringCount > 0 && scope === "earned" && (
        <Banner
          tone="amber"
          icon="clock"
          text={`You have <b>${expiringCount} certification${expiringCount !== 1 ? "s" : ""}</b> expiring within 30 days.`}
          cta="View Renewals"
          onCta={() => setScope("renewals")}
        />
      )}

      <FilterBar
        tabs={filterTabs}
        activeFilter={filter}
        onFilterChange={setFilter}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2.5, borderRadius: "11px" }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <SectionLabel title={scope === "earned" ? "Earned Certifications" : scope === "renewals" ? "Renewals" : "All Certifications"} count={filteredCerts.length} />

      {filteredCerts.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8, color: DS.ink4 }}>
          <VerifiedIcon sx={{ fontSize: 48, mb: 1, color: DS.line }} />
          <Typography variant="h6" fontWeight={700} color={DS.ink3} sx={{ fontFamily: "var(--font-display)" }}>No certifications found</Typography>
          <Typography sx={{ mt: 0.5 }}>
            {filter === "missing" ? "All required certifications are on file." : "No certifications match your current filter."}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }, gap: "18px" }}>
          {filteredCerts.map((item) => (
            <CertificationCard
              key={item.cert?.id || item.certType?.id || Math.random()}
              certType={item.certType}
              cert={item.cert}
              onView={() => openDetails(item.certType, item.cert)}
              onUpload={() => item.certType && openUpload(item.certType)}
              onRenew={() => openDetails(item.certType, item.cert)}
            />
          ))}
        </Box>
      )}

      {dialogs}
    </CertificationShell>
  );
}

// ─── Instructor View ───────────────────────────────────────────────────────
function InstructorView({ userId, tenantId }) {
  const [scope, setScope] = useState("teaching");

  const scopes = [
    { id: "teaching", label: "Teaching Credentials", icon: <SchoolIcon sx={{ fontSize: 16 }} /> },
    { id: "mine", label: "My Certifications", icon: <VerifiedIcon sx={{ fontSize: 16 }} /> },
  ];

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto" }}>
      <ScopeBar scopes={scopes} activeScope={scope} onChange={setScope} />
      {scope === "teaching" ? (
        <StudentView userId={userId} tenantId={tenantId} isInstructor={true} />
      ) : (
        <StudentView userId={userId} tenantId={tenantId} />
      )}
    </Box>
  );
}

// ─── Agency View ───────────────────────────────────────────────────────────
function AgencyView({ tenantId }) {
  const DS = useDS();
  const [scope, setScope] = useState("overview");
  const [personnel, setPersonnel] = useState([]);
  const [, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        // Fetch instructors + students for the tenant
        const [instructors, students] = await Promise.all([
          fetchTenantUsersByRole(tenantId, "INSTRUCTOR").catch(() => []),
          fetchTenantUsersByRole(tenantId, "STUDENT").catch(() => []),
        ]);
        const allUsers = [...instructors, ...students];
        const userIds = allUsers.map((u) => u.id || u.userId).filter(Boolean);
        let certMap = {};
        if (userIds.length > 0) {
          const certs = await fetchCertificationsByUserIds(userIds, tenantId).catch(() => []);
          certMap = certs.reduce((acc, c) => {
            const uid = c.userId;
            if (!acc[uid]) acc[uid] = [];
            acc[uid].push(c);
            return acc;
          }, {});
        }
        const people = allUsers.map((u) => {
          const uid = u.id || u.userId;
          const userCerts = certMap[uid] || [];
          const g = userCerts.filter((c) => certStatus(c) === "active").length;
          const a = userCerts.filter((c) => certStatus(c) === "expiring").length;
          const d = userCerts.filter((c) => certStatus(c) === "expired").length;
          return {
            id: uid,
            name: u.displayName || u.name || uid,
            meta: u.role || "Member",
            station: "\u2014",
            cls: "\u2014",
            g,
            a,
            d,
            status: d > 0 ? "danger" : a > 0 ? "amber" : "green",
            statusLabel: d > 0 ? "Action Required" : a > 0 ? "Needs Attention" : "Compliant",
          };
        });
        setPersonnel(people);
      } catch (e) {
        console.error("Failed to load agency data:", e);
      } finally {
        setLoading(false);
      }
    }
    if (tenantId) load();
  }, [tenantId]);

  const totalPeople = personnel.length;
  const compliant = personnel.filter((p) => p.status === "green").length;
  const expiring = personnel.filter((p) => p.status === "amber").length;
  const action = personnel.filter((p) => p.status === "danger").length;

  const filteredPersonnel = useMemo(() => {
    let items = personnel;
    if (filter !== "all") {
      items = items.filter((p) => p.status === filter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter((p) => p.name.toLowerCase().includes(q));
    }
    return items;
  }, [personnel, filter, searchQuery]);

  const tabs = [
    { id: "all", label: "All", count: filteredPersonnel.length },
    { id: "green", label: "Compliant", count: personnel.filter((p) => p.status === "green").length },
    { id: "amber", label: "Expiring", count: personnel.filter((p) => p.status === "amber").length },
    { id: "danger", label: "Expired", count: personnel.filter((p) => p.status === "danger").length },
  ];

  // Compliance ring SVG
  const pct = totalPeople > 0 ? Math.round((compliant / totalPeople) * 100) : 0;
  const circumference = 2 * Math.PI * 36;
  const dashOffset = circumference - (pct / 100) * circumference;

  return (
    <CertificationShell
      eyebrow="Agency compliance"
      title="Agency Certifications"
      description="Track certification compliance across your organization."
      action={<Button
          variant="contained"
          startIcon={<FileUploadIcon sx={{ fontSize: 18 }} />}
          onClick={() => navigate("/classes/create")}
          sx={{
            height: 40, borderRadius: "11px", textTransform: "none", fontWeight: 700, fontSize: "13.5px",
            px: "18px", bgcolor: DS.primary, backgroundImage: "none", "&:hover": { bgcolor: DS.primary700 },
            boxShadow: "0 1px 0 rgba(20,24,40,.04), 0 1px 3px rgba(20,24,40,.06)",
          }}
        >
          Schedule Class
        </Button>}
      stats={[
        { label: "Personnel", value: totalPeople },
        { label: "Compliant", value: compliant, tone: "success" },
        { label: "Expiring", value: expiring, tone: "warning" },
        { label: "Action required", value: action, tone: "error" },
      ]}
      tabs={[
        { id: "overview", label: "Overview" },
        { id: "personnel", label: "Personnel", count: totalPeople },
      ]}
      activeTab={scope}
      onTabChange={setScope}
    >

      {scope === "overview" && (
        <>
          {expiring > 0 && (
            <Banner
              tone="amber"
              icon="clock"
              text={`You have <b>${expiring} members</b> with expiring certifications. Schedule a renewal class?`}
              cta="Schedule Class"
              onCta={() => navigate("/classes/create")}
            />
          )}

          <Paper elevation={0} sx={{ border: `1px solid ${DS.line}`, borderRadius: "12px", bgcolor: "#fff", boxShadow: "0 1px 0 rgba(20,24,40,.04), 0 1px 3px rgba(20,24,40,.06)", p: { xs: 2, sm: 3 }, mb: 3 }}>
            <Typography sx={{ fontSize: "16px", fontWeight: 700, color: DS.ink, mb: 2, fontFamily: "var(--font-display)" }}>Compliance Overview</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
              <Box sx={{ position: "relative", width: 120, height: 120, flexShrink: 0 }}>
                <svg viewBox="0 0 80 80" style={{ width: "100%", height: "100%", transform: "rotate(-90deg)" }}>
                  <circle cx="40" cy="40" r="36" fill="none" stroke={DS.line3} strokeWidth="7" />
                  <circle cx="40" cy="40" r="36" fill="none" stroke={pct >= 90 ? DS.green : pct >= 70 ? DS.amber : DS.danger} strokeWidth="7" strokeDasharray={circumference} strokeDashoffset={dashOffset} strokeLinecap="round" />
                </svg>
                <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                  <Typography sx={{ fontSize: "24px", fontWeight: 800, color: DS.ink, lineHeight: 1, fontFamily: DS.headlineFont }}>{pct}%</Typography>
                  <Typography sx={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: DS.ink3 }}>compliant</Typography>
                </Box>
              </Box>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: DS.ink, mb: 1 }}>
                  {pct >= 90 ? "Strong compliance" : pct >= 70 ? "Moderate compliance" : "Compliance gaps identified"}
                </Typography>
                <Typography sx={{ fontSize: "13px", color: DS.ink3, lineHeight: 1.6 }}>
                  {pct >= 90
                    ? `Your agency is doing well with ${compliant} out of ${totalPeople} members fully compliant.`
                    : pct >= 70
                    ? `${expiring} member${expiring !== 1 ? "s" : ""} have expiring certs and ${action} member${action !== 1 ? "s" : ""} require immediate attention.`
                    : `${action} member${action !== 1 ? "s" : ""} require immediate action. Schedule renewal classes urgently.`}
                </Typography>
              </Box>
            </Box>
          </Paper>

          <SectionLabel title="Department Breakdown" />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }, gap: "14px" }}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: "12px", border: `1px solid ${DS.line}`, bgcolor: "#fff", boxShadow: "0 1px 0 rgba(20,24,40,.04), 0 1px 3px rgba(20,24,40,.06)" }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 700, color: DS.ink, mb: 1 }}>All Departments</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Box sx={{ flex: 1, height: "6px", borderRadius: "999px", bgcolor: DS.line3, overflow: "hidden" }}>
                  <Box sx={{ height: "100%", borderRadius: "999px", bgcolor: DS.green, width: `${pct}%` }} />
                </Box>
                <Typography sx={{ fontSize: "12px", fontWeight: 700, color: DS.ink3, fontVariantNumeric: "tabular-nums" }}>{pct}%</Typography>
              </Box>
            </Paper>
          </Box>
        </>
      )}

      {scope === "personnel" && (
        <>
          <FilterBar tabs={tabs} activeFilter={filter} onFilterChange={setFilter} searchQuery={searchQuery} onSearchChange={setSearchQuery} />
          <Paper elevation={0} sx={{ borderRadius: "12px", border: `1px solid ${DS.line}`, bgcolor: "#fff", overflow: "hidden" }}>
            {filteredPersonnel.map((p) => (
              <PersonRow key={p.id} person={p} kind="agency" onClick={() => navigate(`/users/${p.id}/certifications`)} />
            ))}
            {filteredPersonnel.length === 0 && (
              <Box sx={{ textAlign: "center", py: 6, color: DS.ink4 }}>
                <PeopleIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography>No personnel found</Typography>
              </Box>
            )}
          </Paper>
        </>
      )}
    </CertificationShell>
  );
}

// ─── Coordinator View ─────────────────────────────────────────────────────
function CoordinatorView({ tenantId }) {
  const DS = useDS();
  const [scope, setScope] = useState("clearance");
  const [instructors, setInstructors] = useState([]);
  const [, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const users = await fetchTenantUsersByRole(tenantId, "INSTRUCTOR").catch(() => []);
        const userIds = users.map((u) => u.id || u.userId).filter(Boolean);
        let certMap = {};
        if (userIds.length > 0) {
          const certs = await fetchCertificationsByUserIds(userIds, tenantId).catch(() => []);
          certMap = certs.reduce((acc, c) => {
            const uid = c.userId;
            if (!acc[uid]) acc[uid] = [];
            acc[uid].push(c);
            return acc;
          }, {});
        }
        const people = users.map((u) => {
          const uid = u.id || u.userId;
          const userCerts = certMap[uid] || [];
          const g = userCerts.filter((c) => certStatus(c) === "active").length;
          const a = userCerts.filter((c) => certStatus(c) === "expiring").length;
          const d = userCerts.filter((c) => certStatus(c) === "expired").length;
          return {
            id: uid,
            name: u.displayName || u.name || uid,
            meta: `${g} active credential${g !== 1 ? "s" : ""}`,
            station: "\u2014",
            cls: "\u2014",
            g,
            a,
            d,
            status: d > 0 ? "danger" : a > 0 ? "amber" : "green",
            statusLabel: d > 0 ? "Action Required" : a > 0 ? "Needs Attention" : "Cleared",
          };
        });
        setInstructors(people);
      } catch (e) {
        console.error("Failed to load coordinator data:", e);
      } finally {
        setLoading(false);
      }
    }
    if (tenantId) load();
  }, [tenantId]);

  const total = instructors.length;
  const cleared = instructors.filter((p) => p.status === "green").length;
  const expiring = instructors.filter((p) => p.status === "amber").length;
  const action = instructors.filter((p) => p.status === "danger").length;

  const filteredInstructors = useMemo(() => {
    let items = instructors;
    if (filter !== "all") {
      items = items.filter((p) => p.status === filter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter((p) => p.name.toLowerCase().includes(q) || p.cls.toLowerCase().includes(q));
    }
    return items;
  }, [instructors, filter, searchQuery]);

  const tabs = [
    { id: "all", label: "All", count: filteredInstructors.length },
    { id: "green", label: "Cleared", count: instructors.filter((p) => p.status === "green").length },
    { id: "amber", label: "Expiring", count: instructors.filter((p) => p.status === "amber").length },
    { id: "danger", label: "Expired", count: instructors.filter((p) => p.status === "danger").length },
  ];

  return (
    <CertificationShell
      eyebrow="Class operations"
      title="Instructor Certifications"
      description="Verify instructor credentials before scheduling classes."
      action={<Button
          variant="contained"
          startIcon={<FileUploadIcon sx={{ fontSize: 18 }} />}
          onClick={() => navigate("/classes/create")}
          sx={{
            height: 40, borderRadius: "11px", textTransform: "none", fontWeight: 700, fontSize: "13.5px",
            px: "18px", bgcolor: DS.primary, backgroundImage: "none", "&:hover": { bgcolor: DS.primary700 },
            boxShadow: "0 1px 0 rgba(20,24,40,.04), 0 1px 3px rgba(20,24,40,.06)",
          }}
        >
          Schedule Class
        </Button>}
      stats={[
        { label: "Instructors", value: total },
        { label: "Cleared", value: cleared, tone: "success" },
        { label: "Expiring", value: expiring, tone: "warning" },
        { label: "Action required", value: action, tone: "error" },
      ]}
      tabs={[
        { id: "clearance", label: "Instructor clearance", count: total },
        { id: "expiring", label: "Expiring soon", count: expiring + action },
      ]}
      activeTab={scope}
      onTabChange={setScope}
    >

      {action > 0 && scope === "clearance" && (
        <Banner
          tone="danger"
          icon="alert"
          text={`You have <b>${action} instructor${action !== 1 ? "s" : ""}</b> with expired certifications who cannot be assigned to classes.`}
          cta="Review Expired"
          onCta={() => setFilter("danger")}
        />
      )}

      <FilterBar tabs={tabs} activeFilter={filter} onFilterChange={setFilter} searchQuery={searchQuery} onSearchChange={setSearchQuery} />

      <Paper elevation={0} sx={{ borderRadius: "12px", border: `1px solid ${DS.line}`, bgcolor: "#fff", overflow: "hidden" }}>
        {filteredInstructors.map((p) => (
          <PersonRow key={p.id} person={p} kind="coordinator" onClick={() => navigate(`/users/${p.id}/certifications`)} />
        ))}
        {filteredInstructors.length === 0 && (
          <Box sx={{ textAlign: "center", py: 6, color: DS.ink4 }}>
            <SchoolIcon sx={{ fontSize: 40, mb: 1 }} />
            <Typography>No instructors found</Typography>
          </Box>
        )}
      </Paper>
    </CertificationShell>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────
export default function CertificationsPage() {
  const DS = useDS();
  const { user, viewAs, activeTenantId } = useTenant();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState("STUDENT");
  const [availableTenants, setAvailableTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState(activeTenantId || "");

  const roles = user?.roles || [];

  useEffect(() => {
    document.documentElement.classList.add("certifications-scrollbar-hidden");
    document.body.classList.add("certifications-scrollbar-hidden");

    return () => {
      document.documentElement.classList.remove("certifications-scrollbar-hidden");
      document.body.classList.remove("certifications-scrollbar-hidden");
    };
  }, []);

  useEffect(() => {
    const urlTab = searchParams.get("tab");
    if (urlTab && ["STUDENT", "INSTRUCTOR", "AGENCY_ADMIN", "COORDINATOR", "ADMIN"].includes(urlTab)) {
      setTab(urlTab);
    } else {
      setTab(viewAs || "STUDENT");
    }
  }, [searchParams, viewAs]);

  useEffect(() => {
    async function loadTenants() {
      try {
        const tenants = await fetchUserMemberships(user?.id);
        const agencyOnes = tenants.filter((m) => m.tenantType === "AGENCY" && m.role !== "STUDENT");
        setAvailableTenants(agencyOnes);
        if (!selectedTenantId && agencyOnes.length > 0) {
          setSelectedTenantId(agencyOnes[0].tenantId);
        }
      } catch (e) {
        console.error("Failed to load tenants", e);
      }
    }
    if (user?.id && (tab === "AGENCY_ADMIN" || tab === "COORDINATOR")) {
      loadTenants();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, tab]);

  const handleTabChange = (newTab) => {
    setTab(newTab);
    setSearchParams({ tab: newTab });
  };

  const tabConfig = [
    { key: "STUDENT", label: "Student", allowed: roles.includes("STUDENT") },
    { key: "INSTRUCTOR", label: "Instructor", allowed: roles.includes("INSTRUCTOR") },
    { key: "AGENCY_ADMIN", label: "Agency", allowed: roles.includes("AGENCY_ADMIN") || roles.includes("ADMIN") || roles.includes("GLOBAL_ADMIN") },
    { key: "COORDINATOR", label: "Coordinator", allowed: roles.includes("COURSE_COORDINATOR") || roles.includes("GLOBAL_ADMIN") },
    { key: "ADMIN", label: "Manage Types", allowed: roles.includes("ADMIN") || roles.includes("GLOBAL_ADMIN") },
  ];
  const allowedTabs = tabConfig.filter((t) => t.allowed);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: { xs: 2, sm: 3 }, px: { xs: 1.5, sm: 3 } }}>
      <CertificationShell>
        {/* Role tabs */}
        {allowedTabs.length > 1 && (
          <Box sx={{ display: "flex", gap: "6px", mb: 3 }}>
            {allowedTabs.map((t) => (
              <Chip
                key={t.key}
                label={t.label}
                onClick={() => handleTabChange(t.key)}
                sx={{
                  fontSize: "12.5px", fontWeight: 700, px: 1.25, py: 0.5,
                  borderRadius: "10px", cursor: "pointer", transition: "all .15s",
                  bgcolor: tab === t.key ? DS.primary : "transparent",
                  color: tab === t.key ? "#fff" : DS.ink3,
                  border: `1px solid ${tab === t.key ? DS.primary : DS.line}`,
                  "&:hover": { borderColor: tab === t.key ? DS.primary : DS.ink4 },
                }}
              />
            ))}
          </Box>
        )}

        {/* Tenant selector for agency/coordinator */}
        {(tab === "AGENCY_ADMIN" || tab === "COORDINATOR") && availableTenants.length > 1 && (
          <Box sx={{ mb: 2 }}>
            <Select
              size="small"
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              sx={{
                minWidth: 220, borderRadius: "10px", bgcolor: "#fff",
                fontSize: "13px", fontWeight: 600, color: DS.ink,
                "& fieldset": { borderColor: DS.line },
              }}
            >
              {availableTenants.map((t) => (
                <MenuItem key={t.tenantId} value={t.tenantId}>{t.tenantName}</MenuItem>
              ))}
            </Select>
          </Box>
        )}

        {tab === "STUDENT" && <StudentView userId={user?.id} tenantId={activeTenantId} />}
        {tab === "INSTRUCTOR" && <InstructorView userId={user?.id} tenantId={activeTenantId} />}
        {tab === "AGENCY_ADMIN" && <AgencyView tenantId={selectedTenantId} />}
        {tab === "COORDINATOR" && <CoordinatorView tenantId={selectedTenantId} />}
        {tab === "ADMIN" && <AdminCertificationTypesView tenantId={selectedTenantId} />}
      </CertificationShell>
    </Box>
  );
}
